import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Confetti from "./Confetti";

export interface TopHostData {
  host_id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  events_attended: number;
}

interface TopHostSlideProps {
  host: TopHostData | null;
}

const TopHostSlide = ({ host }: TopHostSlideProps) => {
  if (!host) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="text-6xl mb-6"
        >
          🎤
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-2xl font-bold text-background mb-2"
        >
          No top host yet
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-background/70"
        >
          Attend more events to find your favorite host!
        </motion.p>
      </div>
    );
  }

  const firstName = host.first_name || "Unknown";
  const lastName = host.last_name || "";
  const initials = `${firstName[0] || ""}${lastName[0] || ""}`.toUpperCase();

  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8 relative">
      <Confetti />
      
      {/* Crown/Star icon */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        className="absolute top-[18%] z-10"
      >
        <Star className="w-12 h-12 text-yellow-400 fill-yellow-400" />
      </motion.div>

      {/* Avatar */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
        className="mb-6 mt-8"
      >
        <Avatar className="w-32 h-32 border-4 border-secondary shadow-lg">
          <AvatarImage src={host.avatar_url || undefined} alt={firstName} />
          <AvatarFallback className="text-3xl font-bold bg-secondary text-secondary-foreground">
            {initials}
          </AvatarFallback>
        </Avatar>
      </motion.div>

      {/* Title */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-background/70 text-lg mb-2"
      >
        Your Top Host
      </motion.p>

      {/* Host name */}
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-4xl font-bold text-background mb-4"
      >
        {firstName}
      </motion.h2>

      {/* Events count */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8 }}
        className="flex items-center gap-2 bg-background/10 rounded-full px-6 py-3"
      >
        <span className="text-3xl font-bold text-background">{host.events_attended}</span>
        <span className="text-background/80 text-lg">
          {host.events_attended === 1 ? "event together" : "events together"}
        </span>
      </motion.div>

      {/* Fun message */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="text-background/60 text-sm mt-6"
      >
        They made your art adventures unforgettable! ✨
      </motion.p>
    </div>
  );
};

export default TopHostSlide;
